<template>
  <div class="d-flex h-100">
    <v-card
      min-height="350"
      elevation="0" outlined
      v-if="!loading && game"
      class="d-flex flex-column h-100 flex-grow-1 justify-start"
      v-bind:class="{'rounded-r-0': xgMap}"
      height="100%"
    >
      <v-card-title class="py-1 px-4 white--text d-flex align-center justify-space-between" style="flex-wrap: nowrap;" :class="$vuetify.theme.dark ? 'black' : 'primary'">
        <div class="d-flex align-center" style="flex: 1 1 auto; min-width: 0;">
          <v-icon color="white" medium class="mr-2">dvr</v-icon>
          <span class="white--text body-font body-1 font-weight-bold text-uppercase text-ellipsis">{{$t("dashboards.lastGame")}}</span>
        </div>
        <v-btn class="pl-0 pr-0" text small :to="`/game/${game.gameId}`" style="flex-shrink: 0; min-width: auto;">
          <span class="white--text text-decoration-underline font-weight-bold mr-1">{{$t('general.seeAll')}}</span>
          <v-icon small color="white">keyboard_arrow_right</v-icon>
        </v-btn>
      </v-card-title>
      
      <!-- League + Date Section -->
      <div class="d-flex align-center justify-center px-4 pt-3 pb-2" v-if="game">
        <img
          v-if="game.leagueId && !game.leagueImgError"
          :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/leagues/' + game.leagueId + '.png'"
          height="18"
          contain
          class="mr-2"
          @error="onImageLoadFailure($event, 'league')"
        />
        <span class="body-2 primary--text font-weight-bold mr-2">{{game.leagueName}}</span>
        <span v-if="game.leagueId" class="mr-2 primary--text">-</span>
        <span class="caption primary--text">{{ getLocalMatchDate(game.matchDate, "DD/MM/YYYY") }}</span>
      </div>

      <!-- Match Score Section with Larger Crests -->
      <div class="d-flex align-center justify-center px-4 py-3">
        <!-- Home Team Crest -->
        <div class="d-flex flex-column align-center mr-6">
          <v-tooltip top color="lightSecondary lighten-4" max-width="300">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" style="display: inline-block;">
                <router-link
                  :to="`/team/${game.homeTeamId}`"
                  class="d-flex align-center justify-center"
                  style="text-decoration: none;"
                >
                  <v-img
                    :src="game.homeTeamImgError ? 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png' : 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + game.homeTeamId + '.png'"
                    :height="$vuetify.breakpoint.smAndDown ? '35' : '45'"
                    :width="$vuetify.breakpoint.smAndDown ? '35' : '45'"
                    contain
                    v-on:error="onImgError($event,'home')"
                  />
                </router-link>
              </div>
            </template>
            <span class="black--text body-1">{{game.homeTeamName}}</span>
          </v-tooltip>
        </div>

        <!-- Score -->
        <router-link
          :to="`/game/${game.gameId}`"
          class="d-flex align-center text-h4 font-weight-bold primary--text"
          style="text-decoration: none; min-width: 80px; justify-content: center;"
        >
          <span>{{game.homeTeamScore}}</span>
          <span class="mx-2">-</span>
          <span>{{game.awayTeamScore}}</span>
        </router-link>

        <!-- Away Team Crest -->
        <div class="d-flex flex-column align-center ml-4">
          <v-tooltip top color="lightSecondary lighten-4" max-width="300">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" style="display: inline-block;">
                <router-link
                  :to="`/team/${game.awayTeamId}`"
                  class="d-flex align-center justify-center"
                  style="text-decoration: none;"
                >
                  <v-img
                    :src="game.awayTeamImgError ? 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png' : 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + game.awayTeamId + '.png'"
                    :height="$vuetify.breakpoint.smAndDown ? '35' : '45'"
                    :width="$vuetify.breakpoint.smAndDown ? '35' : '45'"
                    contain
                    v-on:error="onImgError($event,'away')"
                  />
                </router-link>
              </div>
            </template>
            <span class="black--text body-1">{{game.awayTeamName}}</span>
          </v-tooltip>
        </div>
      </div>

      <v-divider class="my-1"></v-divider>
      <div class="d-flex justify-center" v-if="loadingStats">
        <v-progress-circular :size="35" color="secondary" indeterminate v-if="loadingStats"></v-progress-circular>
      </div>
      <div v-if="homeStats && awayStats && !loadingStats">
        <!-- 1. Expected Goals (xG) -->
        <div class="metric-row py-2 px-4 px-md-6 px-lg-8">
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('xG', homeStats.xG, awayStats.xG, 'home')"
            >{{formatNumberInput(homeStats.xG)}}</span>
          </div>
          <span class="primary--text body-1 font-weight-bold metric-label">{{$t('game.stats.xG')}}</span>
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('xG', homeStats.xG, awayStats.xG, 'away')"
            >{{formatNumberInput(awayStats.xG)}}</span>
          </div>
        </div>
        <v-divider class="my-1"></v-divider>
        
        <!-- 2. Possession -->
        <div class="metric-row py-2 px-4 px-md-6 px-lg-8">
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('possession', homeStats.possession, awayStats.possession, 'home')"
            >{{homeStats.possession ? formatNumberInput(homeStats.possession) + '%' : '-'}}</span>
          </div>
          <span class="primary--text body-1 font-weight-bold metric-label">{{$t('game.stats.possession')}}</span>
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('possession', homeStats.possession, awayStats.possession, 'away')"
            >{{awayStats.possession ? formatNumberInput(awayStats.possession) + '%' : '-'}}</span>
          </div>
        </div>
        <v-divider class="my-1"></v-divider>
        
        <!-- 3. Shots -->
        <div class="metric-row py-2 px-4 px-md-6 px-lg-8">
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('shots', homeStats.shots, awayStats.shots, 'home')"
            >{{formatNumberInput(homeStats.shots)}}</span>
          </div>
          <span class="primary--text body-1 font-weight-bold metric-label">{{$t('game.stats.shots')}}</span>
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('shots', homeStats.shots, awayStats.shots, 'away')"
            >{{formatNumberInput(awayStats.shots)}}</span>
          </div>
        </div>
        <v-divider class="my-1"></v-divider>
        
        <!-- 4. PPDA -->
        <div class="metric-row py-2 px-4 px-md-6 px-lg-8">
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('ppda', homeStats.ppda, awayStats.ppda, 'home')"
            >{{formatNumberInput(homeStats.ppda)}}</span>
          </div>
          <span class="primary--text body-1 font-weight-bold metric-label">{{$t('game.stats.ppda')}}</span>
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('ppda', homeStats.ppda, awayStats.ppda, 'away')"
            >{{formatNumberInput(awayStats.ppda)}}</span>
          </div>
        </div>
        <v-divider class="my-1"></v-divider>
        
        <!-- 5. Expected Points (xP) -->
        <div class="metric-row py-2 px-4 px-md-6 px-lg-8">
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('expectedPoints', homeStats.expectedPoints, awayStats.expectedPoints, 'home')"
            >{{formatNumberInput(homeStats.expectedPoints)}}</span>
          </div>
          <span class="primary--text body-1 font-weight-bold metric-label">{{$t('game.stats.expectedPoints')}}</span>
          <div class="metric-side">
            <span 
              class="body-1 font-weight-medium metric-value metric-value-container" 
              :class="getValueColorClass('expectedPoints', homeStats.expectedPoints, awayStats.expectedPoints, 'away')"
            >{{formatNumberInput(awayStats.expectedPoints)}}</span>
          </div>
        </div>
      </div>
      <div class="d-flex flex-column align-center justify-center" v-if="!loadingStats && (!homeStats || !awayStats)">
        <v-icon color="primary lighten-5" v-bind:style="{'font-size':$vuetify.breakpoint.mdAndUp ? '80px' : '60px'}">blur_on</v-icon>
        <span class="primary--text lighten--text-2 body-1">{{$t("general.dataAvailable")}}</span>
      </div>
    </v-card>
    <v-card class="d-flex flex-grow-1 justify-center align-center py-8 px-12" v-if="loading">
      <v-progress-circular :size="75" color="secondary" indeterminate></v-progress-circular>
    </v-card>
    <v-card
      v-if="xgMap"
      id="chart"
      ref="chart"
      class="text-center d-flex align-center rounded-l-0"
      elevation="2"
    >
      <XGChart
        class="mb-n2"
        v-if="game && xgMap && size != 0 && game != null && shots && shots.length > 0"
        :id="`${game.gameId}`"
        :size="size"
        :game="game"
        :shots="filteredShots"
        :key="xgMapKey"
        :homeTeamName="game.homeTeamName"
        :awayTeamName="game.awayTeamName"
        :circlesAndBoxes="checkboxCircles"
        :language="currentUserLanguage"
      />
    </v-card>
    <v-card elevation="0" outlined v-if="!loading && !game" class="d-flex flex-column flex-grow-1">
      <v-card-title :class="$vuetify.theme.dark ? 'black' : 'primary'" class="py-2 white--text d-flex align-center">
        <v-icon color="white" medium class="mr-2">dvr</v-icon>
        <span class="white--text body-font body-1 font-weight-bold text-uppercase">{{$t("dashboards.lastGame")}}</span>
      </v-card-title>
      <div class="d-flex justify-center align-center flex-column px-3 py-4 mb-13 text-center flex-grow-1">
        <v-icon color="primary lighten-5" v-bind:style="{'font-size':$vuetify.breakpoint.mdAndUp ? '80px' : '60px'}">event_busy</v-icon>
        <span class="primary--text lighten--text-2 body-1">{{$t("dashboards.placeholderNextGame")}}</span>
      </div>
    </v-card>
  </div>
</template>

<script>
import * as d3 from "d3";
import GameService from "../../../services/game/game.service";
import XGChart from "../../base/XGChart";
import ResizeObserver from 'resize-observer-polyfill';
import { getNeutralColors, getLocalMatchDate } from "../../../utils/functions";

export default {
  name: "LastGameWidget",
  components: {
    XGChart
  },
  props: {
    teamId: {
      type: Number,
      required: false
    },
    seasonId: {
      type: Number,
      required: false
    },
    xgMap: {
      type: Boolean,
      default: false
    },
    showAllStats: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    currentUserLanguage() {
      return this.$store.getters["auth/language"];
    },
    homeTeamColor() {
      if (this.game && this.game.homeTeamColor) {
        return this.game.homeTeamColor.replace("0x", "#");
      }
      return getNeutralColors('home')[0];
    },
    awayTeamColor() {
      if (this.game && this.game.awayTeamColor) {
        return this.game.awayTeamColor.replace("0x", "#");
      }
      return getNeutralColors('away')[0];
    }
  },
  data() {
    return {
      game: null,
      homeStats: null,
      awayStats: null,
      loading: false,
      shots: null,
      filteredShots: null,
      checkboxCircles: ["circles"],
      size: 390,
      xgMapKey: 0,
      shotTypeModel:
        "${player.position}" == "GK" ? [15, 16] : [13, 14, 15, 16, 60],
      shotStrategyModel: [22, 23, 215, 24, 25, 26, 160, 123, 124],
      areaModel: [
        "goalArea",
        "boxLeft",
        "boxCenter",
        "boxRight",
        "outBoxDeepLeft",
        "outBoxDeepRight",
        "outBoxLeft",
        "outBoxCentreLeft",
        "outBoxCentre",
        "outBoxCentreRight",
        "outBoxRight",
        "left35",
        "centreLeft35",
        "centre35",
        "centreRight35",
        "right35"
      ],
      bodyPartModel: [15, 20, 21, 72],
      minutes: [0, 90],
      periods: ["period1", "period2"],
      sizeMultiplier: 1.47,
      observer: null,
      loadingStats: false
    };
  },
  filters: {
  },
  created() {
    window.addEventListener("resize", this.onWindowResize);
  },
  mounted() {
    this.size = this.showAllStats ? this.size * this.sizeMultiplier : this.size;
    this.loadViewData();
    this.observer = new ResizeObserver(this.onResize).observe(this.$el);
  },
  methods: {
    async loadViewData() {
      await this.loadGame();
      if (this.xgMap) {
        await this.loadShots();
      }
      this.loadGameSummaryStats();
    },
    async loadGame() {
      this.loading = true;
      if (this.teamId) {
        let gameResponse = await GameService.getLatestGamesPlayed(
          this.teamId,
          1
        );
        this.game = gameResponse.data[0];
      } else if (this.seasonId) {
        let gameResponse = await GameService.getLatestMatchdayGames(
          this.seasonId
        );
        this.game = _.sample(gameResponse.data);
      }
      this.loading = false;
    },
    async loadGameSummaryStats() {
      this.loadingStats = true;
      let gameResponse = await GameService.getGameSummaryStats(
        this.game.gameId
      );
      let stats = gameResponse.data;
      this.homeStats = _.find(stats, s => s.isHomeTeam);
      this.awayStats = _.find(stats, s => !s.isHomeTeam);
      this.loadingStats = false;
    },
    async loadShots() {
      let shotsResponse = await GameService.getXGShots(this.game.gameId);
      this.shots = shotsResponse.data;
      this.updateShots();
      this.onWindowResize();
    },
    onImgError(event, side) {
      if (side === "home") {
        this.game.homeTeamImgError = true;
      } else if (side === "away") {
        this.game.awayTeamImgError = true;
      }
    },
    onImageLoadFailure(event, type) {
      if (type === "league" && event.target) {
        event.target.style.display = "none";
        if (this.game) {
          this.game.leagueImgError = true;
        }
      }
    },
    getLocalMatchDate(date, format) {
      return getLocalMatchDate(date, format);
    },
    getBetterTeam(metric, homeValue, awayValue) {
      // Returns 'home', 'away', or 'tie'
      if (homeValue === null || homeValue === undefined || awayValue === null || awayValue === undefined) {
        return null;
      }
      
      // For PPDA, lower is better
      if (metric === 'ppda') {
        if (homeValue < awayValue) return 'home';
        if (awayValue < homeValue) return 'away';
        return 'tie';
      }
      
      // For all other metrics, higher is better
      if (homeValue > awayValue) return 'home';
      if (awayValue > homeValue) return 'away';
      return 'tie';
    },
    getValueColorClass(metric, homeValue, awayValue, side) {
      const betterTeam = this.getBetterTeam(metric, homeValue, awayValue);
      if (!betterTeam || betterTeam === 'tie') {
        return 'metric-tie';
      }
      if (betterTeam === side) {
        return 'metric-better';
      }
      return 'metric-worse';
    },
    shotsInArea(shots) {
      return shots
        .filter(
          s =>
            this.areaModel.includes("goalArea") ||
            !(s.x >= 94.2 && s.y >= 36.8 && s.y <= 63.2)
        )
        .filter(
          s =>
            this.areaModel.includes("boxRight") ||
            !(s.x >= 83 && s.y >= 21.1 && s.y < 36.8)
        )
        .filter(
          s =>
            this.areaModel.includes("boxCenter") ||
            !(s.x >= 83 && s.x < 94.2 && s.y >= 36.8 && s.y <= 63.2)
        )
        .filter(
          s =>
            this.areaModel.includes("boxLeft") ||
            !(s.x >= 83 && s.y > 63.2 && s.y <= 78.9)
        )
        .filter(
          s =>
            this.areaModel.includes("outBoxDeepRight") ||
            !(s.x >= 83 && s.y < 21.1)
        )
        .filter(
          s =>
            this.areaModel.includes("outBoxDeepLeft") ||
            !(s.x >= 83 && s.y > 78.9)
        )
        .filter(
          s =>
            this.areaModel.includes("outBoxRight") ||
            !(s.x >= 65 && s.x < 83 && s.y < 21.1)
        )
        .filter(
          s =>
            this.areaModel.includes("outBoxCentreRight") ||
            !(s.x >= 65 && s.x < 83 && s.y >= 21.1 && s.y <= 35.5)
        )
        .filter(
          s =>
            this.areaModel.includes("outBoxCentre") ||
            !(s.x >= 65 && s.x < 83 && s.y > 35.5 && s.y <= 64.4)
        )
        .filter(
          s =>
            this.areaModel.includes("outBoxCentreLeft") ||
            !(s.x >= 65 && s.x < 83 && s.y > 64.4 && s.y <= 78.9)
        )
        .filter(
          s =>
            this.areaModel.includes("outBoxLeft") ||
            !(s.x >= 65 && s.x < 83 && s.y > 78.9)
        )
        .filter(
          s =>
            this.areaModel.includes("right35") ||
            !(s.x >= 50 && s.x < 65 && s.y < 21.1)
        )
        .filter(
          s =>
            this.areaModel.includes("centreRight35") ||
            !(s.x >= 50 && s.x < 65 && s.y >= 21.1 && s.y <= 35.5)
        )
        .filter(
          s =>
            this.areaModel.includes("centre35") ||
            !(s.x >= 50 && s.x < 65 && s.y > 35.5 && s.y <= 64.4)
        )
        .filter(
          s =>
            this.areaModel.includes("centreLeft35") ||
            !(s.x >= 50 && s.x < 65 && s.y > 64.4 && s.y <= 78.9)
        )
        .filter(
          s =>
            this.areaModel.includes("left35") ||
            !(s.x >= 50 && s.x < 65 && s.y > 78.9)
        );
    },
    updateShots() {
      var self = this;

      var minutesMaxFilter = this.minutes[1] === 90 ? 150 : this.minutes[1];

      this.filteredShots = this.shots
        .filter(s =>
          self.shotTypeModel.includes(s.eventTypeId)
        )
        .filter(s =>
          self.shotStrategyModel.includes(
            s.strategyId
          )
        )
        .filter(s =>
          self.bodyPartModel.includes(s.bodyPartId)
        )
        .filter(s => _.some(self.periods, p => p.includes(s.periodId)))
        .filter(s => s.min >= self.minutes[0] && s.min < minutesMaxFilter);

      this.filteredShots = this.shotsInArea(this.filteredShots);

      this.xgMapKey += 1;
    },
    onWindowResize(e) {
      if (this.$refs.chart && this.size != this.$refs.chart.clientWidth * this.sizeMultiplier) {
        this.xgMapKey += 1;
      }
    },
    onResize() {
      this.$emit("heightChange", this.$el.clientHeight);
    },
    formatNumberInput(numValue, precision = 1) {
      if (numValue === null || numValue === undefined || isNaN(numValue)) {
        return '-';
      }
      return parseFloat(numValue.toFixed(precision));
    }
  },
  destroyed() {
    d3.selectAll(".tooltip").remove();
    window.removeEventListener("resize", this.onWindowResize);
    delete this.observer;
  }
};
</script>

<style scoped>
.metric-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.metric-side {
  min-width: 60px;
  display: flex;
}

.metric-side:first-child {
  justify-content: flex-start;
}

.metric-side:last-child {
  justify-content: flex-end;
}

.metric-label {
  flex: 1;
  text-align: center;
  padding: 0 8px;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-box-orient: vertical;  
  overflow: hidden;
  line-height: 1.2;
}

.metric-value {
  white-space: nowrap;
}

.metric-value-container {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  text-align: center;
  min-width: 55px;
  white-space: nowrap;
}

.metric-better {
  background-color: #2e7d32 !important; /* Green background */
  color: #ffffff !important; /* White text */
  font-weight: bold !important;
}

.metric-worse {
  background-color: #c62828 !important; /* Red background */
  color: #ffffff !important; /* White text */
  font-weight: bold !important;
}

.metric-tie {
  background-color: rgba(150, 150, 150, 0.9) !important; /* Gray with a bit of opacity background */
  color: white !important;
  font-weight: bold !important;
}
</style>


// WEBPACK FOOTER //
// src/views/dashboard/widgets/LastGameWidget.vue